import { normalizeCustomerNotes } from "@/domain/customer-notes";
import { initialLeadActivities } from "./leadActivityRepository";
import { getDoc, deleteField, runTransaction, serverTimestamp } from "firebase/firestore";
import { AppError, logTechnicalError, toAppError } from "@/data/errors";
import { parseDiscovery } from "@/data/firestore/codec";
import { paths } from "@/data/firestore/paths";
import { cleanForFirestore } from "@/data/firestore/serialize";
import { validateJourney, type PublishedDiscoveryModel } from "@/domain/discovery-model";
import type { Discovery, Lead } from "@/domain/types";
import { resumeJourney } from "@/discovery/journey-state";
import { resolveDiscoveryPath } from "@/domain/discovery-navigation";
import { evaluateDiscovery } from "@/discovery/engine";
import { sendLeadEmail } from "@/lib/backend-api";
import { parsePublishedModel } from "./discoveryModelRepository";
import { presentDisplayedEstimate } from "@/discovery/displayed-estimate";
import {
  consumeEstimateRevisionIntent,
  permitsEstimateRevision,
  type DiscoveryRevisionPermit,
} from "@/discovery/revision-intent";

export async function getDiscovery(
  customerId: string,
  discoveryId: string,
): Promise<Discovery | null> {
  const snapshot = await getDoc(paths.discovery(customerId, discoveryId));
  return snapshot.exists()
    ? parseDiscovery(snapshot.id, snapshot.data({ serverTimestamps: "estimate" }))
    : null;
}

export class LeadNotificationError extends Error {
  constructor(public readonly leadId: string) {
    super("Your details are saved, but the notification request was not confirmed. Retry to finish; your saved request will not be duplicated.");
  }
}

// Share pending work across repeated callers without changing persisted lead data.
const contactSubmissions = new Map<string, Promise<string>>();
export function createLead(
  customerId: string,
  discoveryId: string,
  contact: Lead["contact"],
  requestedAction: Lead["requestedAction"],
  onPersisted?: (leadId: string) => void,
  customerNotes?: string,
): Promise<string> {
  const key = `${customerId}/${discoveryId}`;
  const pending = contactSubmissions.get(key);
  if (pending) return pending;
  const submission = saveLeadAndNotify(customerId, discoveryId, contact, requestedAction, onPersisted, customerNotes)
    .finally(() => { contactSubmissions.delete(key); });
  contactSubmissions.set(key, submission);
  return submission;
}

async function saveLeadAndNotify(
  customerId: string,
  discoveryId: string,
  contact: Lead["contact"],
  requestedAction: Lead["requestedAction"],
  onPersisted?: (leadId: string) => void,
  customerNotes?: string,
): Promise<string> {
  const leadId = crypto.randomUUID();
  const notes = normalizeCustomerNotes(customerNotes);
  let saved: { id: string; created: boolean; mode: string | undefined };
  try {
    saved = await runTransaction(
      paths.customer(customerId).firestore,
      async (transaction) => {
        const discovery = await transaction.get(paths.discovery(customerId, discoveryId));
        if (!discovery.exists()) {
          throw new AppError("missing-document", "This Discovery can no longer create a lead.");
        }
        const existing = discovery.data();
        const mode = (existing["context"] as { mode?: string } | undefined)?.mode;
        if (typeof existing["leadId"] === "string") return { id: existing["leadId"], created: false, mode };
        if (mode !== "live" && mode !== "demo")
          throw new Error("Only live and demo journeys create Leads.");
        if (!existing["result"] || existing["status"] !== "completed")
          throw new Error("Complete and save the Discovery before requesting contact.");
        // Read the pinned version inside this transaction, never today's live model.
        let displayedEstimate: Lead["displayedEstimate"];
        if (typeof existing["modelId"] === "string") {
          const bound = await transaction.get(paths.discoveryModel(customerId, existing["modelId"]));
          if (!bound.exists()) throw new Error("The pinned Discovery model is missing.");
          const model = parsePublishedModel(bound.id, bound.data());
          const session = parseDiscovery(discovery.id, existing);
          displayedEstimate = presentDisplayedEstimate(model.model, session.answers, session.result!.estimate);
        }
        transaction.set(paths.lead(customerId, leadId), {
          leadOrigin: "discovery",
          mode,
          discoveryId,
          sessionId: String(discovery.data()["sessionId"] ?? discoveryId),
          contact,
          requestedAction,
          ...(displayedEstimate ? { displayedEstimate } : {}),
          ...(notes ? { customerNotes: notes } : {}),
          status: "new",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        for (const { id, ...activity } of initialLeadActivities(existing, requestedAction)) {
          transaction.set(paths.leadActivity(customerId, leadId, id), activity);
        }
        transaction.update(paths.discovery(customerId, discoveryId), {
          stage: "sent",
          leadId,
          expiresAt: deleteField(),
          contactRequestedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        transaction.set(paths.log(customerId, crypto.randomUUID()), {
          kind: "lead",
          leadId,
          discoveryId,
          actorId: "visitor",
          summary: {
            en: `${mode === "demo" ? "Demo lead" : "New lead"} from ${contact.name || "website visitor"}`,
            he: `${mode === "demo" ? "ליד הדגמה" : "ליד חדש"} מ-${contact.name || "מבקר באתר"}`,
          },
          baseRevision: null,
          revision: null,
          changes: [],
          createdAt: serverTimestamp(),
        });
        return { id: leadId, created: true, mode };
      },
    );
  } catch (error) {
    const appError = toAppError(error, "write-failed");
    await logTechnicalError({
      customerId,
      operation: "create Lead",
      error: appError,
      category: "data",
      source: "firebase",
      entity: { type: "discovery", id: discoveryId },
    });
    throw appError;
  }

  onPersisted?.(saved.id);
  // Demo Leads persist normally, but are ineligible for business notification emails.
  if (saved.mode === "demo") return saved.id;
  // Retry the existing ID after partial failure; the backend's marker and provider key deduplicate mail.
  try {
    await sendLeadEmail(customerId, saved.id);
  } catch {
    throw new LeadNotificationError(saved.id);
  }
  return saved.id;
}

/** Enter revision atomically without changing answers, result, identity, or Lead linkage. */
export async function beginDiscoveryRevision(
  customerId: string,
  discovery: Discovery,
  model: PublishedDiscoveryModel,
  permit: DiscoveryRevisionPermit,
): Promise<Discovery> {
  if (!permitsEstimateRevision(permit, customerId, discovery, true))
    throw new Error("Improve intent is required to revise this Discovery.");
  const next = await runTransaction(paths.customer(customerId).firestore, async (tx) => {
    const reference = paths.discovery(customerId, discovery.id);
    const snapshot = await tx.get(reference);
    if (!snapshot.exists()) throw new Error("The original Discovery is missing.");
    const current = parseDiscovery(snapshot.id, snapshot.data());
    validateJourney(model.model, current);
    if (
      current.modelId !== model.id ||
      !permitsEstimateRevision(permit, customerId, current) ||
      !current.result
    )
      throw new Error("The completed Discovery cannot enter this revision.");
    if (current.revisionId && current.revisionId !== permit.id)
      throw new Error("This Discovery is already being revised. Reload to resume it.");
    const last = resolveDiscoveryPath(model.model, current.answers).path.at(-1)!;
    const next: Discovery = {
      ...current,
      revisionId: permit.id,
      stage: "questions",
      currentQuestionId: last.question.id,
      currentStepId: last.stepId,
      questionIndex: last.questionIndex,
      updatedAt: new Date(),
    };
    tx.update(reference, {
      revisionId: permit.id,
      stage: next.stage,
      currentQuestionId: next.currentQuestionId,
      currentStepId: next.currentStepId,
      questionIndex: next.questionIndex,
      updatedAt: serverTimestamp(),
    });
    return next;
  });
  consumeEstimateRevisionIntent(permit);
  return next;
}

/** Completed results stay immutable except for a scoped, intentionally opened revision. */
export async function saveBoundDiscovery(
  customerId: string,
  discovery: Discovery,
  model: PublishedDiscoveryModel,
  permit?: DiscoveryRevisionPermit,
): Promise<Discovery> {
  if (!discovery.modelId || discovery.modelId !== model.id)
    throw new Error("Invalid model binding.");
  if (discovery.result) {
    const publicResult = evaluateDiscovery(model.model, discovery.answers);
    if (!publicResult.estimate)
      discovery = { ...discovery, result: { ...publicResult, generatedAt: discovery.result.generatedAt } };
  }
  validateJourney(model.model, discovery);
  return runTransaction(paths.customer(customerId).firestore, async (tx) => {
    const reference = paths.discovery(customerId, discovery.id);
    const snapshot = await tx.get(reference);
    if (!snapshot.exists()) throw new Error("The original Discovery is missing.");
    const current = resumeJourney(model.model, parseDiscovery(snapshot.id, snapshot.data()));
    if (current.modelId !== discovery.modelId) throw new Error("Discovery modelId cannot change.");
    validateJourney(model.model, current);
    const revising =
      permitsEstimateRevision(permit, customerId, current) && current.revisionId === permit.id;
    if ((current.result || current.revisionId) && !revising) return current;
    if (discovery.revisionId && (!revising || discovery.revisionId !== current.revisionId))
      throw new Error("Revision permission does not match this Discovery.");
    if (revising && !discovery.revisionId && (discovery.stage !== "result" || !discovery.result))
      throw new Error("Complete the revised result before closing its revision.");
    if (revising && discovery.result) {
      const ids = Object.keys(current.answers);
      const unchanged =
        ids.length === Object.keys(discovery.answers).length &&
        ids.every(
          (id) => JSON.stringify(current.answers[id]) === JSON.stringify(discovery.answers[id]),
        );
      discovery = {
        ...discovery,
        result:
          unchanged && current.result
            ? current.result
            : {
                ...evaluateDiscovery(model.model, discovery.answers),
                generatedAt: discovery.result.generatedAt,
              },
      };
    }
    if (revising)
      discovery = {
        ...discovery,
        ...(current.leadId ? { leadId: current.leadId } : {}),
        contactRequestedAt: current.contactRequestedAt,
      };

    tx.update(
      reference,
      cleanForFirestore({
        answers: discovery.answers,
        visitorNotes: discovery.visitorNotes ?? "",
        presentedQuestions: discovery.presentedQuestions ?? [],
        questionIndex: discovery.questionIndex,
        currentStepId: discovery.currentStepId,
        currentQuestionId: discovery.currentQuestionId,
        result: discovery.result,
        status: discovery.status,
        stage: discovery.stage,
        firstAnswerAt: discovery.firstAnswerAt,
        completedAt: discovery.completedAt,
        updatedAt: serverTimestamp(),
        ...(revising ? { revisionId: discovery.revisionId ?? deleteField() } : {}),
      }),
    );
    return discovery;
  });
}
